/**
 * App-level ErrorBoundary — prevents whitescreen on React errors
 * Reports to Worker /api/log-error via errorReporter
 */

import React from 'react';
import { reportError } from '../../services/errorReporter';

interface Props { children: React.ReactNode; }
interface State { hasError: boolean; error: Error | null; errorId: string; }

export class AppErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorId: '' };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorId: `BOUNDARY-${Date.now().toString(36)}`,
    };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    reportError({
      message: error.message,
      stack: error.stack,
      action: 'react_boundary',
      inputSummary: `componentStack=${info.componentStack?.slice(0, 500)}`,
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-rose-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-slate-800 mb-2">Có lỗi xảy ra</h1>
          <p className="text-sm text-slate-600 mb-1">
            App gặp lỗi không mong muốn. Lỗi đã được ghi log tự động.
          </p>
          <p className="text-xs text-slate-400 font-mono mb-6">
            ID: {this.state.errorId}
          </p>
          {import.meta.env.DEV && this.state.error && (
            <details className="text-left text-xs text-rose-700 bg-rose-50 p-3 rounded mb-4 max-h-40 overflow-auto">
              <summary className="cursor-pointer font-bold mb-2">Chi tiết (dev only)</summary>
              <pre className="whitespace-pre-wrap">{this.state.error.message}</pre>
              {this.state.error.stack && (
                <pre className="whitespace-pre-wrap mt-2 text-[10px] opacity-70">{this.state.error.stack}</pre>
              )}
            </details>
          )}
          <div className="flex gap-2">
            <button
              onClick={this.handleReload}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-semibold text-sm"
            >
              Thử lại
            </button>
            <button
              onClick={this.handleGoHome}
              className="flex-1 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-semibold text-sm"
            >
              Về trang chủ
            </button>
          </div>
          <p className="text-[11px] text-slate-400 mt-4">
            Nếu lỗi tiếp diễn, liên hệ <a href="mailto:support@dichtailieu.vn" className="text-indigo-600 hover:underline">support@dichtailieu.vn</a>
          </p>
        </div>
      </div>
    );
  }
}
